<template>
  <div class="form-variable-input">
    <!-- Question title -->
    <div v-if="questionTitle" class="text-base font-semibold leading-6 text-gray-900 py-2 mb-2">
      {{ displayQuestionTitle }}
    </div>
    
    <!-- Variable title (fallback if no question) -->
    <div v-else-if="variableTitle" class="text-base font-semibold leading-6 text-gray-900 py-2 mb-2">
      {{ displayVariableTitle }}
    </div>
    
    <!-- Variable description -->
    <div v-if="variableDescription" class="text-gray-600 mb-4">
      {{ displayVariableDescription }}
    </div>
    
    <!-- Text Input -->
    <div v-if="inputType === 'text' || inputType === 'variable'" class="space-y-2">
      <input
        type="text"
        :name="name"
        :value="value"
        @input="handleInput"
        :placeholder="displayPlaceholder"
        class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-0 focus:border-main"
        :class="{ 'opacity-50 cursor-not-allowed': disabled, 'cursor-text': !disabled }"
        :disabled="disabled"
      />
    </div>
    
    <!-- Number Input -->
    <div v-else-if="inputType === 'number'" class="space-y-2">
      <input
        type="number"
        :name="name"
        :value="value"
        @input="handleInput"
        :min="min"
        :max="max"
        :placeholder="displayPlaceholder"
        class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-0 focus:border-main"
        :class="{ 'opacity-50 cursor-not-allowed': disabled, 'cursor-text': !disabled }"
        :disabled="disabled"
      />
    </div>
    
    <!-- Select Input -->
    <div v-else-if="inputType === 'select'" class="space-y-2">
      <select
        :name="name"
        :value="value"
        @change="handleInput"
        class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-0 focus:border-main"
        :class="{ 'opacity-50 cursor-not-allowed': disabled, 'cursor-text': !disabled }"
        :disabled="disabled"
      >
        <option value="">{{ $t('variable.select_placeholder', 'Select an option') }}</option>
        <option 
          v-for="option in options" 
          :key="option.value"
          :value="option.value"
        >
          {{ showMultiLingualText(option.text) }}
        </option>
      </select>
    </div>
  </div>
</template>

<script>
import { showMultiLingualText } from '../../utils/languageUtils.js'

export default {
  name: 'FormVariableInput',
  props: {
    name: {
      type: String,
      required: true
    },
    value: {
      type: [String, Number],
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    questionTitle: {
      type: [String, Object],
      default: ''
    },
    variableTitle: {
      type: [String, Object],
      default: ''
    },
    variableDescription: {
      type: [String, Object],
      default: ''
    },
    inputType: {
      type: String,
      default: 'text',
      validator: function(value) {
        return ['text', 'number', 'select', 'variable'].indexOf(value) !== -1
      }
    },
    placeholder: {
      type: [String, Object],
      default: ''
    },
    min: {
      type: Number,
      default: null
    },
    max: {
      type: Number,
      default: null
    },
    options: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      showMultiLingualText
    }
  },
  computed: {
    displayQuestionTitle() {
      if (this.questionTitle) {
        return this.showMultiLingualText(this.questionTitle, null, false, this.$i18n.locale)
      }
      return ''
    },
    
    displayVariableTitle() {
      if (this.variableTitle) {
        return this.showMultiLingualText(this.variableTitle, null, false, this.$i18n.locale)
      }
      return ''
    },
    
    displayVariableDescription() {
      if (this.variableDescription) {
        return this.showMultiLingualText(this.variableDescription, null, false, this.$i18n.locale)
      }
      return ''
    },
    
    displayPlaceholder() {
      if (this.placeholder) {
        return this.showMultiLingualText(this.placeholder, 'Enter value', false, this.$i18n.locale)
      }
      return ''
    }
  },
  methods: {
    handleInput(event) {
      // Don't allow changes when disabled
      if (this.disabled) {
        event.preventDefault()
        return
      }
      
      // Create a synthetic event object that matches the expected format
      const syntheticEvent = {
        target: {
          name: this.name,
          value: event.target.value
        }
      }
      this.$emit('change', syntheticEvent)
    }
  }
}
</script> 